/**
 * The gate in front of the whole app.
 *
 * Nothing renders until somebody is signed in — not the planner, not the
 * saved routes, not the admin console. It wraps the app rather than sitting
 * inside it so the shell's own startup calls (the health poll, the city list)
 * never fire while there is no session to make them with.
 *
 * The gate is a convenience, not the enforcement: the server refuses every
 * unauthenticated procedure regardless. Hiding the UI only spares an operator
 * a screen where everything fails at once.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';

import { fetchSession, logout, SESSION_EXPIRED_EVENT, type SessionState } from '../api/session';
import { LoginPage } from '../pages/LoginPage';

interface SessionContextValue {
  session: SessionState;
  signOut: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/** Who is signed in, for anything that wants to show or end the session. */
export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error('useSession must be used inside <RequireAuth>');
  }
  return value;
}

type Status = 'checking' | 'in' | 'out' | 'unreachable';

export function RequireAuth({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('checking');
  const [session, setSession] = useState<SessionState>({ authenticated: false });

  const check = useCallback((signal?: AbortSignal) => {
    setStatus('checking');

    fetchSession(signal)
      .then((next) => {
        if (signal?.aborted) return;
        setSession(next);
        setStatus(next.authenticated ? 'in' : 'out');
      })
      .catch(() => {
        if (signal?.aborted) return;
        setStatus('unreachable');
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    check(controller.signal);

    // A token the server refuses mid-session drops straight back to the form
    // rather than leaving a shell where every action fails.
    const onExpired = () => {
      setSession({ authenticated: false });
      setStatus('out');
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);

    return () => {
      controller.abort();
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    };
  }, [check]);

  const signOut = useCallback(() => {
    logout();
    setSession({ authenticated: false });
    setStatus('out');
  }, []);

  if (status === 'checking') {
    return (
      <Box
        sx={{ minHeight: '100dvh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'unreachable') {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Stack spacing={2} sx={{ maxWidth: 400, width: '100%' }}>
          <Alert severity="error">
            Could not reach the TravPlaces API. Check that the server is running, then try again.
          </Alert>
          <Button variant="outlined" onClick={() => check()}>
            Try again
          </Button>
        </Stack>
      </Box>
    );
  }

  if (status === 'out') {
    return <LoginPage onSignedIn={() => check()} />;
  }

  return <SessionContext.Provider value={{ session, signOut }}>{children}</SessionContext.Provider>;
}
